// src/App.js
import { Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Header from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import Benefits from "./components/Benefits";
import ServicesSection from "./components/ServicesSection";
import WhyChooseRevibe from "./components/WhyChooseRevibe";
import Testimonials from "./components/Testimonials";
import HowItWorks from "./components/HowItWorks";
import CTASection from "./components/CTASection";
import Pricing from "./pages/Pricing";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Contact from "./pages/contact";
import PrivacyPolicy from "./pages/policy/PrivacyPolicy";
import ShippingPolicy from "./pages/policy/ShippingPolicy";
import RefundPolicy from "./pages/policy/RefundPolicy";
import TermsConditions from "./pages/policy/TermsConditions";
import About from "./components/About";
import PricingPlan from "./pages/PricingPlan";
import ThankYou from "./pages/ThankYou";
import PaymentFailed from "./pages/PaymentFailed";
import RevibeShortsSection from "./components/RevibeShortsSection";
import Login from "./components/Login";
import Register from "./components/Register";
import Profile from "./WebComponents/Profile";
import RevibeNavbar from "./WebComponents/RevibeNavbar";
import Dashboard from "./WebComponents/Dashboard";
import LiveStream from "./WebComponents/LiveStream";
import SearchPage from "./WebComponents/Search";
import PlanPage from "./WebComponents/Plan";
import WebFooter from "./WebComponents/WebFooter";
import ProtectedRoute from "./WebComponents/ProtectedRoute";
import CheckoutPage from "./WebComponents/Checkout";
import WorkoutDetail from "./WebComponents/Dashboard/Workoutdetail";
import EditProfile from "./WebComponents/Profile/EditProfile";
import ReferPage from "./WebComponents/Profile/ProfilePages/Refer";
import TermsPage from "./WebComponents/Profile/ProfilePages/TearmsCondition";
import ContactPage from "./WebComponents/Profile/ProfilePages/ContactUs";
import AboutPage from "./WebComponents/Profile/ProfilePages/About";
import Livestreamdetail from "./WebComponents/LiveStream/Livestreamdetail"
import MuxStream from "./WebComponents/LiveStream/Muxstreampage"

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Helper function to check if user is fully authenticated
const isUserFullyAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  const isProfileCreated = localStorage.getItem("isProfileCreated") === "true";
  const userData = localStorage.getItem("userData");

  // User is only considered fully authenticated if ALL conditions are met
  return !!(token && isProfileCreated && userData);
};

// HomeRoute: redirects logged-in users to /dashboard, else shows public landing page
const HomeRoute = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    isUserFullyAuthenticated(),
  );

  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(isUserFullyAuthenticated());
    };

    // Check immediately
    checkAuth();

    // Listen for storage events (when localStorage changes in another tab)
    window.addEventListener("storage", checkAuth);
    // Listen for custom user update event
    window.addEventListener("userUpdated", checkAuth);
    // Listen for auth state change event
    window.addEventListener("authStateChanged", checkAuth);

    return () => {
      window.removeEventListener("storage", checkAuth);
      window.removeEventListener("userUpdated", checkAuth);
      window.removeEventListener("authStateChanged", checkAuth);
    };
  }, []);

  // Redirect to dashboard if fully authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show public landing page
  return (
    <>
      <HeroSection />
      <RevibeShortsSection />
      <Benefits />
      <ServicesSection />
      <WhyChooseRevibe />
      <Testimonials />
      <HowItWorks />
      <CTASection />
    </>
  );
};

function App() {
  const [showRevibeNavbar, setShowRevibeNavbar] = useState(false);

  useEffect(() => {
    const checkUserNavbar = () => {
      const token = localStorage.getItem("authToken");
      const isProfileCreated =
        localStorage.getItem("isProfileCreated") === "true";
      const userData = localStorage.getItem("userData");

      // Show RevibeNavbar ONLY if user is fully authenticated
      const shouldShowRevibeNavbar = !!(token && isProfileCreated && userData);

      setShowRevibeNavbar(shouldShowRevibeNavbar);
    };

    // Check immediately
    checkUserNavbar();

    // Listen for custom events and storage changes
    const handleUserUpdated = () => {
      checkUserNavbar();
      // Force a re-render of routes by dispatching a custom event
      window.dispatchEvent(new Event("authStateChanged"));
    };

    window.addEventListener("userUpdated", handleUserUpdated);
    window.addEventListener("storage", checkUserNavbar);
    window.addEventListener("authStateChanged", checkUserNavbar);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdated);
      window.removeEventListener("storage", checkUserNavbar);
      window.removeEventListener("authStateChanged", checkUserNavbar);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="relative">
        {showRevibeNavbar ? <RevibeNavbar /> : <Header />}
        <ScrollToTop />
        <Routes>
          {/* Public Home Route — redirects to /dashboard if logged in */}
          <Route path="/" element={<HomeRoute />} />

          {/* Public Routes */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/pricing-plan" element={<PricingPlan />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/payment-failed" element={<PaymentFailed />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <LiveStream />
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <SearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plan"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <PlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <CheckoutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workout/:id"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <WorkoutDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-profile"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <EditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/refer"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <ReferPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/terms"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <TermsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contactus"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <ContactPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aboutus"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <AboutPage />
              </ProtectedRoute>
            }
          />
            <Route
            path="/live/:id"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <Livestreamdetail/>
              </ProtectedRoute>
            }
          />
           <Route
            path="/mux/:id"
            element={
              <ProtectedRoute requireProfileComplete={true}>
                <MuxStream/>
              </ProtectedRoute>
            }
          />
        </Routes>
        {/* {showRevibeNavbar ? <WebFooter /> : <Footer />} */}
        {!showRevibeNavbar && <Footer />}
      </div>
    </QueryClientProvider>
  );
}

export default App;
